import React from 'react';
import { Layout, FileText, Settings, Sparkles, Mic, RefreshCw, Shield, Brain, Lightbulb, Info, Mail, List, StickyNote, PenLine, Paperclip, AlignLeft } from 'lucide-react';
import Card from '../../../components/Card/Card';
import SectionHeader from '../../../components/SectionHeader/SectionHeader';

function ModelesTab() {
  return (
    <>
      {/* Section: Qu'est-ce qu'un modèle */}
      <Card className="card-large">
        <SectionHeader icon={Layout} title="Les modèles de rapport" variant="default" />
        <div className="guide-section-content">
          <div className="guide-info">
            <FileText className="guide-info-icon" size={20} />
            <p>
              Un <strong>modèle de rapport</strong> définit comment l'IA structure votre dictée.
              Chaque spécialité a ses propres modèles : consultation, écho, ECG, IRM, etc.
            </p>
          </div>
        </div>
      </Card>

      {/* Section: Choisir un modèle */}
      <Card className="card-large">
        <SectionHeader icon={FileText} title="Choisir un modèle" variant="info" />
        <div className="guide-section-content">
          <div className="guide-steps">
            <div className="guide-step">
              <span className="guide-step-number">1</span>
              <div className="guide-step-content">
                <h4>Avant de dicter</h4>
                <p>Sélectionnez le type de rapport dans le dictaphone.</p>
              </div>
            </div>
            <div className="guide-step">
              <span className="guide-step-number">2</span>
              <div className="guide-step-content">
                <h4>Ou laissez l'IA choisir</h4>
                <p>Si vous ne choisissez pas, l'IA détecte automatiquement le type approprié.</p>
              </div>
            </div>
          </div>
          <div className="guide-tip">
            <Lightbulb className="guide-tip-icon" size={20} />
            <p>
              Sélectionner le modèle à l'avance améliore la précision de la transcription
              car l'IA connaît le contexte médical.
            </p>
          </div>
        </div>
      </Card>

      {/* Section: Modèles disponibles */}
      <Card className="card-large">
        <SectionHeader icon={Layout} title="Modèles de base disponibles" variant="success" />
        <div className="guide-section-content">
          <div className="guide-features">
            <div className="guide-feature">
              <div className="guide-feature-icon purple">
                <FileText size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Consultation</h4>
                <p>Rapport structuré avec diagnostic, antécédents, anamnèse, status, discussion, etc.</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon cyan">
                <StickyNote size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Note</h4>
                <p>Comme une note de suite : petit rapport court (anamnèse, status, attitude)</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon orange">
                <PenLine size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Lettre</h4>
                <p>Écrit au format lettre en phrases complètes</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon green">
                <Paperclip size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Annexe</h4>
                <p>Transforme une photo de liste de médicaments, antécédents ou comorbidités en liste structurée</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon blue">
                <Mail size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Mail</h4>
                <p>Génère un mail avec email, objet et contenu + bouton pour l'ouvrir dans votre boîte mail</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon indigo">
                <List size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Liste</h4>
                <p>Modèle qui génère des listes structurées</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon gray">
                <AlignLeft size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Texte</h4>
                <p>Retranscrit purement sans structure (ex : labo court, ECG)</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon brown">
                <FileText size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Et plus...</h4>
                <p>Écho, épreuve d'effort, IRM, scanner, Holter, etc.</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Section: Apprentissage personnalisé */}
      <Card className="card-large">
        <SectionHeader icon={RefreshCw} title="Apprentissage personnalisé" variant="success" />
        <div className="guide-section-content">
          <div className="guide-tip">
            <Sparkles className="guide-tip-icon" size={20} />
            <p>
              Quand vous corrigez un rapport et cliquez sur "Améliorer", l'IA <strong>apprend de vos corrections</strong>.
              Plus vous utilisez voxia, plus les rapports correspondent à votre style.
            </p>
          </div>
          <div className="guide-features">
            <div className="guide-feature">
              <div className="guide-feature-icon indigo">
                <Sparkles size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Modèle personnel</h4>
                <p>Vos corrections entraînent un modèle unique pour vous</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon green">
                <RefreshCw size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Amélioration continue</h4>
                <p>La qualité s'améliore avec chaque utilisation</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Section: Confidentialité */}
      <Card className="card-large">
        <SectionHeader icon={Shield} title="Confidentialité des données" variant="default" />
        <div className="guide-section-content">
          <div className="guide-warning">
            <Shield className="guide-warning-icon" size={20} />
            <p>
              <strong>Vos données restent les vôtres.</strong>
            </p>
          </div>
          <div className="guide-features">
            <div className="guide-feature">
              <div className="guide-feature-icon primary">
                <Shield size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Hébergement suisse</h4>
                <p>Toutes les données sont stockées en Suisse</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon blue">
                <Shield size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>RGPD & LPD</h4>
                <p>Conformité totale aux réglementations européennes et suisses</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon green">
                <Shield size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Pas d'entraînement externe</h4>
                <p>Vos rapports ne servent jamais à entraîner d'IA générale</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Section: Personnalisation */}
      <Card className="card-large">
        <SectionHeader icon={Settings} title="Personnaliser les modèles" variant="info" />
        <div className="guide-section-content">
          <span className="guide-badge-gold">Gold / Enterprise</span>
          <div className="guide-info" style={{ marginTop: '15px' }}>
            <Settings className="guide-info-icon" size={20} />
            <p>
              Avec le plan Gold, vous pouvez <strong>personnaliser chaque modèle</strong>
              avec des instructions spécifiques : style d'écriture, niveau de détail, formulations préférées, status normal habituel, etc.
            </p>
          </div>
        </div>
      </Card>

      {/* Section: Instructions personnalisées */}
      <Card className="card-large">
        <SectionHeader icon={Sparkles} title="Instructions personnalisées" variant="default" />
        <div className="guide-section-content">
          <div className="guide-steps">
            <div className="guide-step">
              <span className="guide-step-number">1</span>
              <div className="guide-step-content">
                <h4>Allez dans Réglages</h4>
                <p>Section "Modèles de rapport".</p>
              </div>
            </div>
            <div className="guide-step">
              <span className="guide-step-number">2</span>
              <div className="guide-step-content">
                <h4>Cliquez sur le crayon</h4>
                <p>À côté du modèle à personnaliser.</p>
              </div>
            </div>
            <div className="guide-step">
              <span className="guide-step-number">3</span>
              <div className="guide-step-content">
                <h4>Écrivez vos instructions</h4>
                <p>Ex: "Quand je dis status normal, écris ça: ...", "Style concis", etc.</p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </>
  );
}

export default ModelesTab;
